import { CompanyServices } from '@services/company-services';
import { defineStore } from 'pinia';
import { EventLogger } from '@utils/event-metrics';
import { useInterface } from '@stores';
import { EVENTS_DICTIONARY } from '@assets/constants/events-dictionary';
import { TravelsPageName } from '@assets/constants/route-names';
import { useTravelsPage } from './travels-page-store';

const events = EVENTS_DICTIONARY[TravelsPageName];

export const useTravelBuses = defineStore('TravelBuses', {
  state: () => ({
    error: null,
    newBus: null,
    group: null,
    isInvalidForm: null,
    isModalOpen: false,
    isConfirmationOpen: false,
    currentFreightValue: null,
    newFreightValue: null,
    invoiceIssueDate: null,
    reasonPlateExchance: null,
    explanationPlateExchance: null,
  }),
  getters: {
    currentBus(state) {
      return state?.group?.onibus;
    },
    busError(state) {
      return state.error?.message;
    },
    busStatus(state) {
      return state.error?.status;
    },
    needApproval() {
      return this.busStatus === 'PRECISA_APROVACAO';
    },
  },
  actions: {
    openEditBusModal(group, newBus, error) {
      this.group = group;
      this.newBus = newBus;
      this.error = error;
      this.isModalOpen = true;
    },
    closeEditBusModal() {
      this.error = null;
      this.isInvalidForm = null;
      this.reasonPlateExchance = null;
      this.explanationPlateExchance = null;
      this.isModalOpen = false;
    },
    openConfirmation(group, newBus, freight) {
      this.group = group;
      this.newBus = newBus;
      this.currentFreightValue = freight?.current_value ?? null;
      this.newFreightValue = freight?.new_value ?? null;
      this.closeEditBusModal();
      this.isConfirmationOpen = true;
    },
    close() {
      this.error = null;
      this.newBus = null;
      this.group = null;
      this.isInvalidForm = null;
      this.isModalOpen = false;
      this.isConfirmationOpen = false;
      this.currentFreightValue = null;
      this.newFreightValue = null;
      this.invoiceIssueDate = null;
      this.reasonPlateExchance = null;
      this.explanationPlateExchance = null;
    },
    askSetGroupBus() {
      const $interfaceState = useInterface();
      const TravelsPageStore = useTravelsPage();

      return CompanyServices.askSetGroupBus(
        this.newBus?.id,
        this.group?.id,
        this.reasonPlateExchance,
        this.explanationPlateExchance,
      )
        .then(() => {
          TravelsPageStore.requestGroups();
          $interfaceState.toast.add({ label: 'Troca de ônibus solicitada', color: 'success' });
          this.close();
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.error || error.message;
          $interfaceState.toast.add({ label: errorMessage, color: 'danger' });
        });
    },
    async askFreightReductionAndConfirm(group, bus) {
      const $interfaceState = useInterface();

      try {
        const freight = await CompanyServices.calculateFreightReduction(bus?.id, group?.id);
        return this.openConfirmation(group, bus, freight);
      } catch (error) {
        const isBackendError = Boolean(error) && !(error instanceof Error);
        const errorMessage = (isBackendError && (error.error || error.message))
          || 'Não foi possível mostrar o novo valor do frete. A troca foi realizada e o frete será ajustado.';
        $interfaceState.toast.add({ label: errorMessage, color: 'danger' });
        return this.trySetBus(group, bus);
      }
    },
    trySetBus(group, bus) {
      const $interfaceState = useInterface();
      const TravelsPageStore = useTravelsPage();
      this.isConfirmationOpen = false;

      return CompanyServices.setGroupBus(
        bus?.id,
        group?.id,
      )
        .then(() => {
          $interfaceState.toast.add({ label: 'Ônibus escalado com sucesso', color: 'success' });
          TravelsPageStore.requestGroups();
        })
        .catch((error) => {
          const OnlyToastErrors = ['DOCUMENTACAO_NAO_APROVADA', 'MICRO_ONIBUS_OR_VAN']
          if (!error || OnlyToastErrors.includes(error.status)) {
            $interfaceState.toast.add({ label: error?.message || 'Erro ao escalar o ônibus', color: 'danger' });
          } else {
            this.openEditBusModal(group, bus, error);
          }
        });
    },
  },
});
